/**
 * LetterTemplatesPage — Full-page letter template management.
 * Lists all letter templates with create/edit/delete actions.
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Pencil, Trash2, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import lettersApi, { LetterTemplate } from '@/services/lettersApi';
import { LetterTemplateEditor } from './LetterTemplateEditor';

interface Props {
  onBack?: () => void;
}

const TYPE_BADGES: Record<string, { bg: string; label: string }> = {
  welcome: { bg: 'bg-paper-2 text-ink-700', label: 'Welcome' },
  utr: { bg: 'bg-paper-2 text-ink-700', label: 'Unable to Reach' },
  closure: { bg: 'bg-paper-2 text-ink-700', label: 'Closure' },
  notice: { bg: 'bg-paper-2 text-ink-700', label: 'Notice' },
  custom: { bg: 'bg-paper-2 text-ink-700', label: 'Custom' },
};

export function LetterTemplatesPage({ onBack }: Props) {
  const tabbed = useTabbedChrome();
  const [templates, setTemplates] = useState<LetterTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [deleting, setDeleting] = useState<number | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await lettersApi.listTemplates();
      setTemplates(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this letter template?')) return;
    setDeleting(id);
    try {
      await lettersApi.deleteTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch {
      // ignore
    } finally {
      setDeleting(null);
    }
  };

  const handleEdit = (id: number) => {
    setEditingId(id);
    setEditorOpen(true);
  };

  const handleCreate = () => {
    setEditingId(undefined);
    setEditorOpen(true);
  };

  const handleSaved = () => {
    setEditorOpen(false);
    setEditingId(undefined);
    fetchTemplates();
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <div>
            {!tabbed && (
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Letter Templates
            </h1>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Create and manage HTML letter templates for print-and-mail outreach via PostGrid/Lob.
            </p>
          </div>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>

      {/* Template List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading templates...
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 dark:text-gray-400 mb-1">No letter templates yet</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">
                Create templates with merge fields like {'{{member_first_name}}'} for personalized mail.
              </p>
              <Button size="sm" onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" /> Create First Template
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Merge Fields</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map(tpl => {
                  const badge = TYPE_BADGES[tpl.template_type] || TYPE_BADGES.custom;
                  return (
                    <TableRow
                      key={tpl.id}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => handleEdit(tpl.id)}
                    >
                      <TableCell className="font-medium">{tpl.template_name}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.bg}`}>
                          {badge.label}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(tpl.merge_fields || []).slice(0, 4).map(f => (
                            <Badge key={f} variant="outline" className="text-xs">{`{{${f}}}`}</Badge>
                          ))}
                          {(tpl.merge_fields || []).length > 4 && (
                            <Badge variant="outline" className="text-xs">+{(tpl.merge_fields || []).length - 4}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {tpl.created_at ? new Date(tpl.created_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(tpl.id)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={deleting === tpl.id}
                            onClick={() => handleDelete(tpl.id)}
                          >
                            {deleting === tpl.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Editor Dialog */}
      {editorOpen && (
        <LetterTemplateEditor
          templateId={editingId}
          onClose={() => { setEditorOpen(false); setEditingId(undefined); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

export default LetterTemplatesPage;
